import { TextInput, PlanBadge, tokens, Link, Text } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { useCurrentSite, useCurrentSiteId } from '@hotjar/site';
import { useCallback } from 'react';
import { noop } from '@hotjar/webapp/lib/function';
import { useGate, useTierForGate } from '@hotjar/allowances';
import { useTranslation } from 'react-i18next';
import { FEEDBACK_LABEL_MAX_LENGTH } from '@hotjar/feedback/domain/IncomingFeedback/Feedback';
import { WizardLabel } from '@hotjar/feedback/components/common/Wizard/WizardLabel';

const Wrapper = styled.div`
  margin-bottom: ${tokens.spacer.x2};
`;

const Caption = styled.div`
  ${tokens.text('caption')}
  color: ${tokens.color.text_light};
  margin-bottom: ${tokens.spacer.x1};
`;

const StyledPlanBadge = styled(PlanBadge)`
  margin-left: ${tokens.spacer.x1};
`;

const ResetButton = styled(Link)<{ show: boolean }>`
  ${tokens.text('caption')}
  margin-top: ${tokens.spacer.x1};
  color: ${tokens.color.text_default};
  display: ${(p) => (p.show ? 'inline' : 'none')};
  flex-shrink: 0;
`;

const ValidationText = styled(Text)`
  margin-top: ${tokens.spacer.x1};
  width: 100%;
  display: block;
`;

export interface WidgetLabelFieldProps {
  widgetLabel: string | null;
  onWidgetLabelChange: (widgetLabel: string | null) => void;
}

export function WidgetLabelField({
  widgetLabel,
  onWidgetLabelChange,
}: WidgetLabelFieldProps): JSX.Element | null {
  const { t } = useTranslation('feedback');
  const { data: site } = useCurrentSite();
  const gateContextId = useCurrentSiteId();
  const gateContext = { id: gateContextId, type: 'site' } as const;
  const hasFeature = useGate('feedback.appearance.widget_label', gateContext);
  const plan = useTierForGate('feedback.appearance.widget_label', gateContext);

  const onUpsell = useCallback(() => {
    if (!site) return;
    upsell({ site, referrer: 'feedback.appearance.widget_label' }).catch(noop);
  }, [site]);

  if (!site) return null;

  /* TODO: Enhance this by getting translation for Feedback string from CS */
  const defaultFeedbackLabel = 'Feedback';

  const isInvalid = hasFeature !== true && widgetLabel !== null;

  return (
    <Wrapper>
      <WizardLabel htmlFor="widget-label">
        {t('wizard.section.appearance.label.label')}
        {hasFeature !== true && plan && <StyledPlanBadge kind={plan} size="s" onClick={onUpsell} />}
      </WizardLabel>
      <Caption id="widget-label-caption">
        {t('wizard.section.appearance.label.hint', { length: FEEDBACK_LABEL_MAX_LENGTH })}{' '}
      </Caption>

      <TextInput
        maxLength={FEEDBACK_LABEL_MAX_LENGTH}
        aria-describedby="widget-label-caption"
        size="m"
        isDisabled={hasFeature !== true}
        placeholder={defaultFeedbackLabel}
        id="widget-label"
        isInvalid={isInvalid}
        value={widgetLabel ?? ''}
        onChange={(e) => onWidgetLabelChange(e.target.value || null)}
      />
      {isInvalid && (
        <ValidationText textType="caption" textColor="text_danger">
          {t('wizard.section.appearance.label.validation-text')}
        </ValidationText>
      )}
      <ResetButton
        as="button"
        type="button"
        kind="inline"
        onClick={() => onWidgetLabelChange(null)}
        show={widgetLabel !== null}
      >
        {t('wizard.section.appearance.label.reset')}
      </ResetButton>
    </Wrapper>
  );
}
